// Mobil uygulama kabuğu: üst çubuk, alt sekme çubuğu, alt sayfa (sheet).
//
// Telefonda portal native uygulama gibi davranmalı. Apple HIG'e göre birincil
// gezinme alt sekme çubuğuyla yapılır (3-5 sekme) ve hamburger menü birincil
// gezinmede kullanılmaz — keşfedilebilirliği düşürüyor. Masaüstünde kenar
// çubuğu aynen kalır; bu modül yalnızca dar ekranda devreye girer.

import { esc, icons, settingsLabel } from './helpers';
import { api } from './api';

// Bağımlılıklar dışarıdan verilir: app.ts <-> shell.ts döngüsel import olmasın.
type Deps = {
  state: any;
  isAdmin: () => boolean;
  navigate: (path: string) => void;
  logout: () => void;
};
let D: Deps;
export function provideShellDeps(d: Deps) { D = d; }

type Tab = { href: string; label: string; icon: string };

const TABS_ADMIN: Tab[] = [
  { href: '/dashboard', label: 'Panel', icon: 'layout-dashboard' },
  { href: '/clients', label: 'Müşteriler', icon: 'users' },
  { href: '/invoices', label: 'Faturalar', icon: 'file-text' },
  { href: '/finance', label: 'Finans', icon: 'landmark' },
];

const TABS_CUSTOMER: Tab[] = [
  { href: '/dashboard', label: 'Panel', icon: 'layout-dashboard' },
  { href: '/projects', label: 'Projeler', icon: 'folder-kanban' },
  { href: '/invoices', label: 'Faturalar', icon: 'file-text' },
  { href: '/contracts', label: 'Sözleşmeler', icon: 'file-signature' },
];

// Sekmeye sığmayan bölümler "Daha fazla" sayfasında toplanır.
const MORE_ADMIN: Tab[] = [
  { href: '/projects', label: 'Projeler', icon: 'folder-kanban' },
  { href: '/payments', label: 'Tahsilatlar', icon: 'wallet' },
  { href: '/contracts', label: 'Sözleşmeler', icon: 'file-signature' },
  { href: '/documents', label: 'Dokümanlar', icon: 'file-archive' },
  { href: '/tickets', label: 'Destek', icon: 'life-buoy' },
  { href: '/report', label: 'Yıllık Rapor', icon: 'calculator' },
  { href: '/activity', label: 'İşlem Günlüğü', icon: 'scroll-text' },
];

const MORE_CUSTOMER: Tab[] = [
  { href: '/payments', label: 'Tahsilatlar', icon: 'wallet' },
  { href: '/documents', label: 'Dokümanlar', icon: 'file-archive' },
  { href: '/tickets', label: 'Destek', icon: 'life-buoy' },
];

const isPhone = () => window.matchMedia('(max-width: 768px)').matches;

// ─── Alt sayfa (sheet) ──────────────────────────────────────────────────────

let openSheetEl: HTMLElement | null = null;

export function closeSheet() {
  document.getElementById('sheet-backdrop')?.remove();
  openSheetEl?.remove();
  openSheetEl = null;
  document.removeEventListener('keydown', onSheetKey);
}

function onSheetKey(e: KeyboardEvent) {
  if (e.key === 'Escape') closeSheet();
}

/** Alttan açılan sayfa. `dismissible=false` ise kapatılamaz (imza kapısı gibi). */
export function openSheet(title: string, bodyHTML: string, opts: { dismissible?: boolean; action?: { label: string; onClick: () => void } } = {}) {
  closeSheet();
  const dismissible = opts.dismissible !== false;

  const back = document.createElement('div');
  back.className = 'sheet-backdrop';
  back.id = 'sheet-backdrop';
  if (dismissible) back.addEventListener('click', closeSheet);

  const sheet = document.createElement('div');
  sheet.className = 'sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.innerHTML =
    '<div class="sheet-grabber"></div>'
    + '<div class="sheet-head">'
    + (dismissible ? '<button class="sheet-action" id="sheet-close">Kapat</button>' : '<span></span>')
    + '<div class="sheet-title">' + esc(title) + '</div>'
    + (opts.action ? '<button class="sheet-action right" id="sheet-action">' + esc(opts.action.label) + '</button>' : '<span></span>')
    + '</div>'
    + '<div class="sheet-body">' + bodyHTML + '</div>';

  document.body.appendChild(back);
  document.body.appendChild(sheet);
  openSheetEl = sheet;
  sheet.querySelector('#sheet-close')?.addEventListener('click', closeSheet);
  if (opts.action) sheet.querySelector('#sheet-action')?.addEventListener('click', opts.action.onClick);
  if (dismissible) document.addEventListener('keydown', onSheetKey);
  icons();
  return sheet;
}

function rowsHTML(items: Tab[]): string {
  return '<div class="ios-list">' + items.map(t =>
    '<a class="ios-row" href="' + esc(t.href) + '" data-sheet-nav>'
    + '<span class="ios-row-icon"><i data-lucide="' + esc(t.icon) + '"></i></span>'
    + '<span class="ios-row-main"><span class="ios-row-title">' + esc(t.label) + '</span></span>'
    + '<span class="ios-row-value"><i data-lucide="chevron-right"></i></span></a>').join('') + '</div>';
}

function bindSheetNav(sheet: HTMLElement) {
  sheet.querySelectorAll('[data-sheet-nav]').forEach(a => a.addEventListener('click', (e) => {
    e.preventDefault();
    const href = (a as HTMLAnchorElement).getAttribute('href')!;
    closeSheet();
    D.navigate(href);
  }));
}

function openMore() {
  const items = D.isAdmin() ? MORE_ADMIN : MORE_CUSTOMER;
  const sheet = openSheet('Daha fazla', '<div class="ios-group">' + rowsHTML(items) + '</div>');
  bindSheetNav(sheet);
}

function openAccount() {
  const u = D.state.user;
  const sheet = openSheet('Hesabım',
    '<div class="ios-group"><div class="ios-list">'
    + '<div class="ios-row"><span class="ios-row-icon"><i data-lucide="user"></i></span>'
    + '<span class="ios-row-main"><span class="ios-row-title">' + esc(u?.name || '') + '</span>'
    + '<span class="ios-row-sub">' + esc(u?.email || '') + '</span></span><span></span></div>'
    + '</div></div>'
    + '<div class="ios-group">'
    + '<div class="ios-list">'
    + '<a class="ios-row" href="/settings" data-sheet-nav><span class="ios-row-icon"><i data-lucide="settings"></i></span>'
    + '<span class="ios-row-main"><span class="ios-row-title">' + esc(settingsLabel(D.isAdmin())) + '</span></span>'
    + '<span class="ios-row-value"><i data-lucide="chevron-right"></i></span></a>'
    + '<button class="ios-row" id="sheet-logout"><span class="ios-row-icon" style="background:var(--danger-soft);color:var(--danger)"><i data-lucide="log-out"></i></span>'
    + '<span class="ios-row-main"><span class="ios-row-title" style="color:var(--danger)">Çıkış yap</span></span><span></span></button>'
    + '</div></div>');
  bindSheetNav(sheet);
  sheet.querySelector('#sheet-logout')?.addEventListener('click', () => { closeSheet(); D.logout(); });
}

// ─── Alt sekme çubuğu ───────────────────────────────────────────────────────

export function buildTabBar() {
  const bar = document.getElementById('tab-bar');
  if (!bar) return;
  const tabs = D.isAdmin() ? TABS_ADMIN : TABS_CUSTOMER;
  bar.style.gridTemplateColumns = 'repeat(' + (tabs.length + 1) + ', 1fr)';
  bar.innerHTML = tabs.map(t =>
    '<a href="' + esc(t.href) + '" data-page="' + esc(t.href.slice(1)) + '">'
    + '<i data-lucide="' + esc(t.icon) + '"></i>' + esc(t.label) + '</a>').join('')
    + '<a href="#" id="tab-more"><i data-lucide="ellipsis"></i>Daha fazla</a>';
  bar.querySelectorAll('a[data-page]').forEach(a => a.addEventListener('click', (e) => {
    e.preventDefault();
    D.navigate((a as HTMLAnchorElement).getAttribute('href')!);
  }));
  bar.querySelector('#tab-more')?.addEventListener('click', (e) => { e.preventDefault(); openMore(); });
  icons();
}

// ─── Üst çubuk ──────────────────────────────────────────────────────────────

let backTarget: string | null = null;

/** Breadcrumb'dan beslenir: son öğe başlık, bir öncekisi geri hedefi olur. */
export function syncAppBar(items: Array<{ label: string; href?: string }>) {
  const titleEl = document.getElementById('app-bar-title');
  const backBtn = document.getElementById('app-back') as HTMLButtonElement | null;
  const backLabel = document.getElementById('app-back-label');
  if (!titleEl || !backBtn || !backLabel) return;

  const last = items[items.length - 1];
  titleEl.textContent = last?.label || 'Leadup';

  const av = document.getElementById('app-bar-avatar');
  if (av) av.textContent = (D.state.user?.name || '?').charAt(0).toUpperCase();

  const prev = items.length > 1 ? items[items.length - 2] : null;
  if (prev?.href) {
    backTarget = prev.href;
    backLabel.textContent = prev.label;
    backBtn.hidden = false;
    document.getElementById('app-bar-logo')?.setAttribute('hidden', '');
  } else {
    backTarget = null;
    backBtn.hidden = true;
    document.getElementById('app-bar-logo')?.removeAttribute('hidden');
  }
}

function onContentScroll() {
  const bar = document.getElementById('app-bar');
  const content = document.getElementById('content');
  if (!bar || !content) return;
  bar.classList.toggle('is-condensed', content.scrollTop > 10);
}

export function initShell() {
  buildTabBar();
  document.getElementById('app-back')?.addEventListener('click', () => {
    if (backTarget) D.navigate(backTarget); else history.back();
  });
  document.getElementById('app-action')?.addEventListener('click', openAccount);
  document.getElementById('content')?.addEventListener('scroll', onContentScroll, { passive: true });
  // Yön değişiminde sekme çubuğu görünürlüğü CSS'e bırakıldı; JS inline stil
  // yazmıyor, böylece cihaz döndürülünce bozulmuyor.
  window.addEventListener('orientationchange', () => setTimeout(onContentScroll, 60));
}

export { isPhone };

// ─── İmza kapısı ────────────────────────────────────────────────────────────
// İmzalanmamış sözleşmesi olan müşteri portalı kullanamaz. Kapı tam ekrandır,
// kapatma yolu yoktur ve her sayfa geçişinde kendini yeniden dayatır; tek
// istisna o sözleşmenin imza ekranıdır — yoksa imzalamak da mümkün olmazdı.

let pendingContracts: Array<{ id: number; title: string }> = [];

export async function refreshContractGate() {
  if (D.isAdmin()) { pendingContracts = []; removeGate(); return; }
  try {
    const d = await api.get('/contracts');
    pendingContracts = (d.contracts || [])
      .filter((c: any) => c.status === 'sent')
      .map((c: any) => ({ id: c.id, title: c.title }));
  } catch {
    pendingContracts = [];   // Liste alınamazsa kullanıcıyı kilitleme
  }
  enforceContractGate(location.pathname);
}

// İletişim adresi kodda gömülü değil; admin'in girdiği şirket ayarından gelir.
function noteHTML(): string {
  const mail = D.state.settings?.email;
  if (!mail) return '<p class="gate-note">Sorunuz varsa imzalamadan önce bizimle iletişime geçebilirsiniz.</p>';
  return '<p class="gate-note">Sorunuz varsa imzalamadan önce bize yazabilirsiniz: '
    + '<a href="mailto:' + esc(mail) + '">' + esc(mail) + '</a></p>';
}

function removeGate() {
  document.getElementById('contract-gate')?.remove();
}

function blockKeys(e: KeyboardEvent) {
  // Escape ile kapatılamaz; sekme tuşu kapının içinde kalır.
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); }
}

export function enforceContractGate(path: string) {
  if (!pendingContracts.length) { removeGate(); document.removeEventListener('keydown', blockKeys, true); return; }
  // İmza ekranındayken kapı çekilir, aksi hâlde imzalanamaz.
  const onSigningPage = pendingContracts.some(c => path === '/contracts/' + c.id);
  if (onSigningPage) { removeGate(); return; }
  if (document.getElementById('contract-gate')) return;

  const first = pendingContracts[0];
  const many = pendingContracts.length > 1;
  const gate = document.createElement('div');
  gate.id = 'contract-gate';
  gate.className = 'gate';
  gate.setAttribute('role', 'dialog');
  gate.setAttribute('aria-modal', 'true');
  gate.innerHTML =
    '<div class="gate-inner">'
    + '<img src="/brand/logo.png" alt="Leadup" class="gate-logo">'
    + '<h1 class="gate-title">İmzanız bekleniyor</h1>'
    + '<p class="gate-text">'
    + (many
        ? esc(String(pendingContracts.length)) + ' sözleşmeniz imza bekliyor. Portalı kullanmaya başlamak için önce bunları imzalamanız gerekiyor.'
        : 'Bir sözleşmeniz imza bekliyor. Portalı kullanmaya başlamak için önce bu sözleşmeyi imzalamanız gerekiyor.')
    + '</p>'
    + '<div class="gate-list">' + pendingContracts.map(c =>
        '<div class="gate-item"><i data-lucide="file-signature"></i><span>' + esc(c.title) + '</span></div>').join('') + '</div>'
    + '<button class="btn btn-primary btn-block" id="gate-open">'
    + (many ? 'İlk sözleşmeyi aç ve imzala' : 'Sözleşmeyi aç ve imzala') + '</button>'
    + noteHTML()
    + '</div>';
  document.body.appendChild(gate);
  document.addEventListener('keydown', blockKeys, true);
  gate.querySelector('#gate-open')?.addEventListener('click', () => {
    removeGate();
    D.navigate('/contracts/' + first.id);
  });
  icons();
}


// ─── Katmanlı akış ──────────────────────────────────────────────────────────
// Uzun formu tek ekranda kaydırtmak yerine, her adımı ekrana sığan ayrı bir
// katman olarak gösterir. Mobil uygulama mantığı: kaydırmayı azaltmak.

export type FlowStep = {
  title: string;
  body: string;                                   // HTML (dinamik değerler esc'li)
  primaryLabel?: string;                          // yoksa ilerleme satır seçimiyle olur
  onMount?: (root: HTMLElement, go: (n: number) => void) => void;
  onPrimary?: (root: HTMLElement, go: (n: number) => void) => void | Promise<void>;
};

export function openFlow(steps: FlowStep[], onClose?: () => void) {
  const el = document.createElement('div');
  el.className = 'layer';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  document.body.appendChild(el);

  let idx = 0;
  const close = () => { el.remove(); onClose?.(); };

  const go = (n: number) => {
    if (n < 0) { close(); return; }
    if (n >= steps.length) { close(); return; }
    idx = n;
    const st = steps[idx];
    el.innerHTML =
      '<div class="layer-head">'
      + '<button class="sheet-action" id="layer-back">' + (idx === 0 ? 'Vazgeç' : 'Geri') + '</button>'
      + '<span class="layer-title">' + esc(st.title) + '</span>'
      + '<span class="layer-step">' + (idx + 1) + '/' + steps.length + '</span>'
      + '</div>'
      + '<div class="layer-body">' + st.body + '</div>'
      + (st.primaryLabel
          ? '<div class="layer-foot"><button class="btn btn-primary" id="layer-next">' + esc(st.primaryLabel) + '</button></div>'
          : '');
    icons();
    el.querySelector('#layer-back')?.addEventListener('click', () => go(idx - 1));
    el.querySelector('#layer-next')?.addEventListener('click', () => st.onPrimary?.(el, go));
    st.onMount?.(el, go);
  };

  go(0);
  return { go, close, root: el };
}
